<template>
    <div class="editor-container">
        <div class="px-1">
            <AppEditableText :value="field.label" @update="onLabelUpdate" class="font-18 d-inline text-semi-bold">
                <div slot="popover-header" class="text-muted text-left">
                    Field name
                    <TeamLanguagePlaceholdersRef class="mb-1"/>
                </div>
                <template slot="popover-footer" slot-scope="{value}">
                    <div v-if="!value" class="text-danger text-left font-12">
                        Value is required
                    </div>
                </template>
            </AppEditableText>

            <AppConfirmationPopover
                v-if="!isDocTypeField"
                class="float-right"
                message="Delete field?"
                :action="() => $emit('delete')">
                <div class="w-100 text-danger text-center clickable">
                    <i class="fal fa-trash-alt"></i>
                </div>
            </AppConfirmationPopover>

            <div class="text-muted">
                [{{ field.path }}]
            </div>
        </div>

        <div class="editor-section">
            <div class="editor-section-badge">
                General
            </div>
            <div class="custom-control custom-switch">
                <input class="custom-control-input" type="checkbox"
                       :value="field.visible"
                       :checked="field.visible"
                       @change="updateProp('visible', !field.visible)"
                       id="visible">
                <label class="custom-control-label text-dark pt-1" for="visible">
                    Visible
                </label>
            </div>
            <div class="custom-control custom-switch">
                <input class="custom-control-input" type="checkbox"
                       :value="field.editable"
                       :checked="field.editable"
                       @change="updateProp('editable', !field.editable)"
                       id="editable">
                <label class="custom-control-label text-dark pt-1" for="editable">
                    Editable
                </label>
            </div>
        </div>

        <div class="editor-section">
            <div class="editor-section-badge">
                Input
            </div>
            <FieldInputEditor :field="field" @update="v => updateProp('editor', v)"/>
        </div>

        <div class="editor-section">
            <div class="editor-section-badge">
                View
            </div>
            <div v-if="!isLineField" class="custom-control custom-switch mb-2">
                <input class="custom-control-input" type="checkbox"
                       :value="renderer.horizontal"
                       :checked="renderer.horizontal"
                       @change="updateProp(['renderer', 'horizontal'], !renderer.horizontal)"
                       id="horizontal">
                <label class="custom-control-label text-dark pt-1" for="horizontal">
                    Horizontal
                </label>
            </div>
            <div v-if="!isLineField" class="form-group">
                <label>Form columns</label>
                <vue-number-input
                    :value="section.cols"
                    @change="v => updateProp(['section', 'cols'], v)"
                    :min="2" :max="12"
                    controls
                    size="small">
                </vue-number-input>
            </div>
            <FieldWidthEditor
                :key="field.path"
                :value="renderer.width"
                :pixelsOnly="isLineField"
                class="form-group"
                @update="v => updateProp(['renderer', 'width'], v)"/>

            <div v-if="!isLineField" class="form-group">
                <label>Align</label>
                <AppRadioButtonsGroup
                    label="label"
                    trackBy="value"
                    :value="renderer.align"
                    :options="alignOptions"
                    @update="v => updateProp(['renderer', 'align'], v)"/>
            </div>
            <div v-if="!isLineField">
                <label>
                    Break line
                </label>
                <div class="custom-control custom-checkbox">
                    <input class="custom-control-input" type="checkbox"
                           :value="lineBreak.before"
                           :checked="lineBreak.before"
                           @change="updateProp(['renderer', 'break', 'before'], !lineBreak.before)"
                           id="break-line-before">
                    <label class="custom-control-label text-dark pt-1" for="break-line-before">
                        Before
                    </label>
                </div>
                <div class="custom-control custom-checkbox mb-2">
                    <input class="custom-control-input" type="checkbox"
                           :value="lineBreak.after"
                           :checked="lineBreak.after"
                           @change="updateProp(['renderer', 'break', 'after'], !lineBreak.after)"
                           id="break-line-after">
                    <label class="custom-control-label text-dark pt-1" for="break-line-after">
                        After
                    </label>
                </div>
            </div>
        </div>

        <div v-if="!isDocTypeField" class="editor-section">
            <div class="editor-section-badge">
                Validations
            </div>
            <FieldValidationsEditor
                :field="field"
                :key="field.path"
                @update="v => updateProp('validations', v)"/>
        </div>

        <div class="editor-section">
            <div class="editor-section-badge">
                Rules
            </div>
            <FieldRulesEditor
                :field="field"
                :key="`rules-${field.path}`"
                @update="v => updateProp('rules', v)"/>
        </div>

        <div v-if="showActionsSection.length !== 0" class="editor-section">
            <div class="editor-section-badge">
                Actions
            </div>
            <FieldActionsEditor
                :field="field"
                :key="`actions-${field.path}`"
                @update="v => updateProp('actions', v)"/>
        </div>

        <div v-if="showDependenciesSection" class="editor-section">
            <div class="editor-section-badge">
                Dependencies
            </div>
            <FieldDependantsEditor
                :field="field"
                :key="`dependants-${field.path}`"
                @update="v => updateProp('dependants', v)"/>
        </div>

        <slot name="editor-footer"></slot>
    </div>
</template>

<script>
import AppRadioButtonsGroup from '../../../../../../components/shared/AppRadioButtonsGroup'
import {FormSystemSections} from '@/types/document-viewer/section'
import {FieldRendererAlign} from '@/types/document-viewer/field/modules/renderer'
import FieldWidthEditor from './components/FieldWidthEditor'
import FieldInputEditor from './components/input/FieldInputEditor'
import FieldValidationsEditor from './components/validation/FieldValidations'
import AppEditableText from '../../../../../../components/shared/AppEditableText'
import AppConfirmationPopover from '../../../../../../components/shared/AppConfirmationPopover'
import FieldActionsEditor from './components/actions/FieldActionsEditor'
import {fieldActionModels} from '@/types/document-viewer/field/modules/action'
import {FieldEditorTypes} from '@/types/document-viewer/field/modules/editor'
import FieldDependantsEditor from './components/dependants/FieldDependantsEditor'
import TeamLanguagePlaceholdersRef from '@/views/settings/team/lang/TeamLanguagePlaceholdersRef'
import FieldRulesEditor from '@/views/documents/subtypes/settings/editor/field/components/rules/FieldRulesEditor.vue'

export default {
    name: 'FieldEditor',
    components: {
        FieldRulesEditor,
        TeamLanguagePlaceholdersRef,
        FieldDependantsEditor,
        FieldActionsEditor,
        AppConfirmationPopover,
        AppEditableText,
        FieldValidationsEditor,
        FieldWidthEditor,
        AppRadioButtonsGroup,
        FieldInputEditor
    },
    props: {
        field: {
            type: Object,
            required: true
        },
        updateField: {
            type: Function,
            required: true
        }
    },
    data() {
        return {}
    },
    computed: {
        renderer() {
            return this.field.renderer || {}
        },

        editor() {
            return this.field.editor || {}
        },

        section() {
            return this.field.section || {}
        },

        isLineField() {
            return [FormSystemSections.LINES, FormSystemSections.LANDED_COST_LINES].includes(this.section.key)
        },

        isAllocationField() {
            return this.section.key === FormSystemSections.ALLOCATIONS
        },

        isCompanyName() {
            return this.field.path === 'company.name'
        },

        isStockCode() {
            return this.field.path === 'lines.sku'
        },

        isDocTypeField() {
            return this.field.path === 'type'
        },

        isSelectType() {
            return [FieldEditorTypes.DD_SINGLE, FieldEditorTypes.DD_MULTIPLE].includes(this.editor.type)
        },

        showDependenciesSection() {
            return this.isSelectType || this.isStockCode || this.isCompanyName
        },

        alignOptions() {
            return [
                {value: FieldRendererAlign.LEFT, label: 'Left'},
                {value: FieldRendererAlign.CENTER, label: 'Center'},
                {value: FieldRendererAlign.RIGHT, label: 'Right'}
            ]
        },

        lineBreak() {
            return this.renderer.break || {}
        },

        showActionsSection() {
            return fieldActionModels.filter(a => a.available.validator(this.field))
        }
    },
    methods: {
        updateProp(path, value) {
            if (_.isEqual(_.get(this.field, path), value)) {
                return
            }
            const field = _.set(_.cloneDeep(this.field), path, value)
            this.updateField(field)
        },

        onLabelUpdate(value) {
            if (value) {
                this.updateProp('label', value)
            }
        }
    },
    watch: {
        'editor.type': {
            handler(type) {
                if (this.field.dependants && this.field.dependants.length > 0 && !this.showDependenciesSection) {
                    this.updateProp('dependants', [])
                }
            },
            deep: true
        }
    }
}
</script>

<style lang="scss" scoped>
@import "src/views/documents/subtypes/settings/editor/styles";
</style>
